/**
 * Shared number formatting utilities for devenv debug UI.
 *
 * Canonical versions of formatNumber, formatTokenAmount, and formatEth also
 * live in @gotts.ai/ui for reuse by other packages.
 */

interface FormatNumberOptions {
  compact?: boolean;
  decimals?: number;
  prefix?: string;
}

interface FormattedValue {
  display: string;
  full: string;
}

export function formatNumber(
  value: number,
  opts: FormatNumberOptions = {},
): FormattedValue {
  const { compact = true, decimals = 2, prefix = "" } = opts;
  const full = `${prefix}${value.toLocaleString("en-US", { maximumFractionDigits: 18 })}`;

  if (!compact)
    return {
      display: `${prefix}${value.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`,
      full,
    };

  const abs = Math.abs(value);
  let display: string;
  if (abs >= 1e9)
    display = `${prefix}${(value / 1e9).toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}B`;
  else if (abs >= 1e6)
    display = `${prefix}${(value / 1e6).toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}M`;
  else if (abs >= 1e3)
    display = `${prefix}${(value / 1e3).toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}K`;
  else if (abs < 0.0001 && abs > 0)
    display = `${prefix}${value.toExponential(2)}`;
  else
    display = `${prefix}${value.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;

  return { display, full };
}

export function formatTokenAmount(
  raw: bigint,
  decimals: number,
  opts: FormatNumberOptions = {},
): FormattedValue {
  const num = Number(raw) / 10 ** decimals;
  return formatNumber(num, opts);
}

export function formatEth(value: number): string {
  if (value >= 1e6) return formatNumber(value, { compact: true }).display;
  if (value < 0.0001 && value > 0) return value.toExponential(2);
  // Smart formatting: commas for large numbers, appropriate decimals
  if (value >= 1000) {
    const hasDecimals = value % 1 !== 0;
    return value.toLocaleString("en-US", {
      minimumFractionDigits: hasDecimals ? 2 : 0,
      maximumFractionDigits: 2,
    });
  }
  return value.toLocaleString("en-US", {
    minimumFractionDigits: value < 1 ? 4 : 2,
    maximumFractionDigits: value < 1 ? 4 : 2,
  });
}

/** Returns em-dash for zero values, otherwise delegates to formatNumber */
export function displayValue(
  value: number,
  opts: FormatNumberOptions = {},
): string {
  if (value === 0) return "\u2014";
  return formatNumber(value, opts).display;
}

export function formatPrice(value: number): string {
  return `$${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function formatDelta(value: number): {
  text: string;
  className: string;
} {
  const sign = value >= 0 ? "+" : "";
  const text = `${sign}${value.toFixed(2)}`;
  const className =
    value > 0
      ? "text-green-400"
      : value < 0
        ? "text-red-400"
        : "text-text-muted";
  return { text, className };
}

/**
 * Format a token amount for display, truncating to appropriate decimals.
 * - >= 1: 4 decimal places
 * - < 1: 6 decimal places
 * Returns both a truncated `display` and the `full` original precision.
 */
export function formatTokenDisplay(
  value: string | number,
  maxDecimals = 6,
): { display: string; full: string } {
  const num = typeof value === "string" ? parseFloat(value) : value;
  const full = typeof value === "string" ? value : String(value);

  if (isNaN(num) || num === 0) return { display: "0", full: full || "0" };

  const abs = Math.abs(num);
  if (abs < 0.000001) return { display: num.toExponential(2), full };

  const decimals = abs >= 1 ? Math.min(4, maxDecimals) : maxDecimals;
  const display = num.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  });
  return { display, full };
}

/**
 * Smart truncation for all UI amount displays.
 * - >= 1000: comma-separated, 2 decimal places ("998.49", "1,234.56")
 * - >= 1 and < 1000: up to 4 decimal places ("0.3768")
 * - < 1 and >= 0.0001: up to 6 decimal places
 * - < 0.0001: scientific notation
 * Returns `{ display, full }` like formatTokenDisplay.
 */
export function formatDisplayAmount(value: string | number): {
  display: string;
  full: string;
} {
  const num = typeof value === "string" ? parseFloat(value) : value;
  const full = typeof value === "string" ? value : String(value);

  if (isNaN(num) || num === 0) return { display: "0", full: full || "0" };

  const abs = Math.abs(num);
  if (abs < 0.0001) return { display: num.toExponential(2), full };

  if (abs >= 1000) {
    const display = num.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return { display, full };
  }
  if (abs >= 1) {
    const display = num.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 4,
    });
    return { display, full };
  }
  // < 1 and >= 0.0001
  const display = num.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 6,
  });
  return { display, full };
}

/**
 * Truncated CTA label: "Swap ~998.49 WETH → ~0.3768 USDC"
 */
export function formatSwapButtonLabel(
  amountIn: string | number,
  tokenIn: string,
  amountOut: string | number,
  tokenOut: string,
): string {
  const inDisplay = formatDisplayAmount(amountIn).display;
  const outDisplay = formatDisplayAmount(amountOut).display;
  return `Swap ~${inDisplay} ${tokenIn} \u2192 ~${outDisplay} ${tokenOut}`;
}

/**
 * Fee with context: "$5,990.94 (0.30% of input)"
 */
export function formatFeeWithPercent(usdAmount: number, pct: string): string {
  return `${formatUsd(usdAmount)} (${pct} of input)`;
}

/**
 * Format a number as a USD string.
 * - Zero: "$0.00"
 * - Tiny positive (< $0.01): "<$0.01"
 * - Otherwise: "$1,234.56"
 * - When `compact` is true, abbreviate large values: "$600.0B", "$45.8M", "$305.5K"
 */
export function formatUsd(value: number, opts?: { compact?: boolean }): string {
  if (value === 0) return "$0.00";
  if (value > 0 && value < 0.01) return "<$0.01";
  if (opts?.compact) {
    const abs = Math.abs(value);
    if (abs >= 1e9)
      return `$${(value / 1e9).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}B`;
    if (abs >= 1e6)
      return `$${(value / 1e6).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}M`;
    if (abs >= 1e3)
      return `$${(value / 1e3).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}K`;
  }
  return `$${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Always-compact USD formatting for summary cards and legends.
 * Values >= $1K are abbreviated: "$600.0B", "$45.8M", "$305.5K".
 * Values < $1K use standard 2-decimal formatting.
 */
export function formatUsdCompact(value: number): string {
  return formatUsd(value, { compact: true });
}

/**
 * Smart timestamp: time-only for today, date+time for older.
 */
export function formatSmartTime(timestamp: number): string {
  const date = new Date(timestamp);
  const now = new Date();
  const isToday =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();
  if (isToday) {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
  return (
    date.toLocaleDateString([], { month: "short", day: "numeric" }) +
    " " +
    date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
  );
}

/**
 * Compute the minimum received amount after slippage.
 */
export function computeMinimumReceived(
  amountOut: number,
  slippageBps: number,
): number {
  return amountOut * (1 - slippageBps / 10000);
}

/**
 * Format a balance for display — never compact K/M/B notation.
 * Always comma-separated: 2 decimals for >= 1, 4 for >= 0.01, 6 for smaller.
 */
export function formatBalanceDisplay(value: number): string {
  if (value === 0) return "0.00";
  const abs = Math.abs(value);
  if (abs < 0.0001) return value.toExponential(2);
  const decimals = abs >= 1 ? 2 : abs >= 0.01 ? 4 : 6;
  return value.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Format a portfolio percentage: "45.2%", "0.05%", "< 0.01%", or "0%".
 * Shows 2 decimal places for values >= 0.01% to distinguish small allocations.
 */
export function formatPortfolioPercent(value: number, total: number): string {
  if (total === 0 || value === 0) return "0%";
  const pct = (value / total) * 100;
  if (pct < 0.01) return "< 0.01%";
  if (pct < 0.1) return `${pct.toFixed(2)}%`;
  return `${pct.toFixed(1)}%`;
}

/**
 * Format a relative time: "just now", "5s ago", "2m ago", etc.
 */
export function formatRelativeTime(timestamp: number): string {
  const diff = Math.max(0, Math.floor((Date.now() - timestamp) / 1000));
  if (diff < 5) return "just now";
  if (diff < 60) return `${String(diff)}s ago`;
  const mins = Math.floor(diff / 60);
  if (mins < 60) return `${String(mins)}m ago`;
  const hrs = Math.floor(mins / 60);
  return `${String(hrs)}h ago`;
}

/**
 * Compact balance display with tooltip support.
 * Returns `{ display, full }` — `display` is abbreviated, `full` is comma-separated.
 */
export function formatBalanceCompact(
  value: number,
  symbol: string,
): { display: string; full: string } {
  const full = formatBalanceDisplay(value);
  if (value === 0) return { display: "0", full };

  const abs = Math.abs(value);
  const isStable = STABLECOINS_SET.has(symbol.toUpperCase());

  if (abs >= 1e9) {
    const d = (value / 1e9).toLocaleString("en-US", {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    });
    return { display: `${d}B`, full };
  }
  if (abs >= 1e6) {
    const d = (value / 1e6).toLocaleString("en-US", {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    });
    return { display: `${d}M`, full };
  }
  if (abs >= 1e3) {
    const d = (value / 1e3).toLocaleString("en-US", {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    });
    return { display: `${d}K`, full };
  }

  const decimals = isStable ? 2 : abs >= 1 ? 2 : 4;
  const display = value.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return { display, full };
}

/** Format gas as compact number: "5.52M", "920K" */
export function formatGasCompact(gas: number): string {
  return formatNumber(gas, { compact: true, decimals: 2 }).display;
}

/** Format gas summary: "5.52M · Avg: 920K" */
export function formatGasSummary(total: number, avg: number): string {
  return `${formatGasCompact(total)} \u00b7 Avg: ${formatGasCompact(avg)}`;
}

/**
 * Format a raw uint256 liquidity value with compact suffixes.
 * Handles the enormous numbers from V3/V4 positions.
 * Caps at T (trillion) since P/E suffixes are not widely recognized.
 */
export function formatLiquidity(liquidity: bigint): string {
  const n = Number(liquidity);
  if (n === 0) return "0";
  // Everything above 1T stays in T units (e.g. 2.11e15 → "2,110T")
  if (n >= 1e12) {
    const trillions = n / 1e12;
    return trillions >= 10
      ? `${Math.round(trillions).toLocaleString()}T`
      : `${trillions.toFixed(2)}T`;
  }
  if (n >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(2)}K`;
  return n.toFixed(0);
}

/**
 * Convert a Uniswap fee value (e.g. 3000) to a display percentage ("0.30%").
 * Uniswap fees are in hundredths of a bip (1/1,000,000).
 */
export function formatFeePercent(fee: number): string {
  return `${(fee / 10000).toFixed(2)}%`;
}

/**
 * Pure string truncation of decimal places — no rounding.
 * "49.9999999999999999" with maxDecimals=6 → "49.999999"
 */
export function truncateDecimals(value: string, maxDecimals = 6): string {
  const dotIdx = value.indexOf(".");
  if (dotIdx === -1) return value;
  return value.slice(0, dotIdx + 1 + maxDecimals);
}

/**
 * Compact fee notation: "2/20" for 200 bps mgmt / 2000 bps perf.
 */
export function formatFeeNotation(mgmtBps: number, perfBps: number): string {
  return `${(mgmtBps / 100).toFixed(0)}/${(perfBps / 100).toFixed(0)}`;
}

const STABLECOINS_SET = new Set(["USDC", "USDT", "DAI"]);

const STABLECOINS = new Set(["USDC", "USDT", "DAI"]);

/**
 * Format a token balance with symbol-aware precision.
 * Stablecoins (USDC, USDT, DAI): 2 decimal places.
 * Crypto tokens (ETH, WETH, WBTC, etc.): 4 decimal places.
 * Supports compact mode for large values (e.g. "1.08M").
 */
export function formatTokenBalance(
  value: number | string,
  symbol: string,
  opts: { compact?: boolean } = {},
): string {
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num) || num === 0) return "0";

  const decimals = STABLECOINS.has(symbol.toUpperCase()) ? 2 : 4;

  if (opts.compact) {
    return formatNumber(num, { compact: true, decimals }).display;
  }

  return num.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  });
}
